<template>
  <form @submit.prevent="nextStep">
    <div class="grid grid-cols-1 gap-20 lg:grid-cols-3">
      <div
        v-for="(fieldBlock, fieldBlockIndex) in fieldBlocks"
        :key="fieldBlockIndex"
      >
        <div
          class="mb-20 last:mb-0"
          v-for="(fieldBlockItem, fieldBlockItemIndex) in fieldBlock"
          :key="fieldBlockItemIndex"
        >
          <h3 class="text-10 text-black mb-6" v-if="fieldBlockItem.title">
            {{ fieldBlockItem.title }}
          </h3>

          <div
            class="mb-4 last:mb-0"
            v-for="field in fieldBlockItem.fields"
            :key="field.name"
          >
            <component
              :is="`${field.component}Index`"
              :label="field.placeholder || field.label"
              :options="field.options || []"
              :maxLength="field.maxLength || undefined"
              v-model="field.value"
              @change="fieldChange(field)"
              v-if="orderEditMode"
            />
            <component
              :is="field.component"
              :label="field.label"
              :placeholder="field.placeholder"
              :options="field.options || []"
              :vertical="field.vertical || false"
              :rules="field.rules"
              :error="field.error || false"
              :disabled="orderEditMode || loading"
              :maxLength="field.maxLength || undefined"
              v-model="field.value"
              @change="fieldChange(field)"
              v-else
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mt-16">
      <Price />

      <div class="mt-10" v-if="askCancelConfirmation">
        <Alert>{{ __("cancel_confirmation_text") }}</Alert>

        <div class="flex justify-end flex-wrap -mx-6 -mb-6 mt-10">
          <div class="w-full p-6 md:w-1/2 lg:w-128">
            <Button
              variant="third"
              size="full"
              :disabled="cancelLoading"
              @click="stopCancel"
            >
              {{ __("btn_back") }}
            </Button>
          </div>

          <div class="w-full p-6 md:w-1/2 lg:w-128">
            <Button
              type="button"
              variant="danger"
              size="full"
              :loading="cancelLoading"
              @click="cancel"
            >
              {{ __("btn_cancel_booking") }}
            </Button>
          </div>
        </div>
      </div>

      <div class="flex justify-end flex-wrap -mx-6 -mb-6 mt-10" v-else>
        <div
          class="w-full p-6 md:w-1/2 lg:w-128"
          v-if="isAdmin && !orderEditMode"
        >
          <Button
            variant="danger"
            size="full"
            :loading="lockTimeLoading"
            @click="lockTime"
          >
            {{ __("btn_lock_time") }}
          </Button>
        </div>

        <div class="w-full p-6 md:w-1/2 lg:w-128">
          <Button
            variant="danger"
            size="full"
            :disabled="loading"
            @click="startCancel"
          >
            {{ __("btn_cancel_booking") }}
          </Button>
        </div>

        <div class="w-full p-6 md:w-1/2 lg:w-128">
          <Button
            type="submit"
            variant="secondary"
            size="full"
            :loading="loading"
          >
            {{ __("btn_next") }}
          </Button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { ref } from "vue";
import {
  cancelOrder,
  orderEditMode,
  isAdmin,
  adminBlockTime
} from "@/stores/Order";
import { setRoomView, setAmountServiceView } from "@/stores/View";
import {
  userDetailValues,
  setUserDetailValue,
  saveUserDetails
} from "@/stores/UserDetails";

import __ from "@/libraries/Translation";

import Alert from "@/components/Alert";
import Button from "@/components/Button";
import TextField from "@/components/Form/TextField";
import PhoneField from "@/components/Form/PhoneField";
import BooleanGroupField from "@/components/Form/BooleanGroupField";
import TextareaField from "@/components/Form/TextareaField";

import TextFieldIndex from "@/components/Form/Index/TextField";
import PhoneFieldIndex from "@/components/Form/Index/PhoneField";
import BooleanGroupFieldIndex from "@/components/Form/Index/BooleanGroupField";
import TextareaFieldIndex from "@/components/Form/Index/TextareaField";

import fieldValidator from "@/libraries/FieldValidator";
import { loadAmountServices, loadCateringServices } from "@/stores/Services";
import Price from "@/components/Price";

export default {
  components: {
    Alert,
    Button,
    TextField,
    PhoneField,
    BooleanGroupField,
    TextareaField,
    TextFieldIndex,
    PhoneFieldIndex,
    BooleanGroupFieldIndex,
    TextareaFieldIndex,
    Price
  },
  setup() {
    const loading = ref(false);

    const fieldBlocks = ref([
      [
        {
          title: __("form.birthday_child"),
          fields: [
            {
              component: "TextField",
              id: "birthday_child_name",
              name: "birthday_child_name",
              label: "",
              placeholder: __("form.name"),
              value: userDetailValues.value["birthday_child_name"] || null,
              rules: ["required"],
              maxLength: 50
            },
            {
              component: "TextField",
              id: "birthday_child_age",
              name: "birthday_child_age",
              label: "",
              placeholder: __("form.age"),
              value: userDetailValues.value["birthday_child_age"] || null,
              rules: ["required", "numeric"]
            }
          ]
        },
        {
          title: __("form.gender"),
          fields: [
            {
              component: "BooleanGroupField",
              id: "birthday_child_gender",
              name: "birthday_child_gender",
              label: "",
              placeholder: "",
              vertical: true,
              options: [
                {
                  value: __("form.boy"),
                  label: __("form.boy")
                },
                {
                  value: __("form.girl"),
                  label: __("form.girl")
                }
              ],
              value: userDetailValues.value["birthday_child_gender"] || null,
              rules: ["required"]
            }
          ]
        }
      ],
      [
        {
          title: __("form.responsible_adult"),
          fields: [
            {
              component: "TextField",
              id: "responsible_adult_name",
              name: "responsible_adult_name",
              label: "",
              placeholder: __("form.name"),
              value: userDetailValues.value["responsible_adult_name"] || null,
              rules: ["required"],
              maxLength: 50
            },
            {
              component: "PhoneField",
              id: "responsible_adult_phone",
              name: "responsible_adult_phone",
              label: "",
              placeholder: __("form.phone"),
              value: userDetailValues.value["responsible_adult_phone"] || null,
              rules: ["required"]
            }
          ]
        },
        {
          title: __("form.payment"),
          fields: [
            {
              component: "BooleanGroupField",
              id: "payment_type",
              name: "payment_type",
              label: "",
              placeholder: "",
              vertical: true,
              options: [
                {
                  value: 1,
                  label: __("form.up_front_payment")
                },
                {
                  value: 2,
                  label: __("form.full_payment")
                }
              ],
              value: userDetailValues.value["payment_type"] || null,
              rules: ["required"]
            }
          ]
        }
      ],
      [
        {
          title: __("form.comment"),
          fields: [
            {
              component: "TextareaField",
              id: "comment",
              name: "comment",
              label: "",
              placeholder: "",
              value: userDetailValues.value["comment"] || null,
              rules: []
            }
          ]
        }
      ]
    ]);

    const nextStep = async () => {
      loading.value = true;

      let anyErrors = false;

      fieldBlocks.value.forEach(fieldBlock => {
        fieldBlock.forEach(fieldBlockItem => {
          fieldBlockItem.fields.forEach(field => {
            const success = fieldValidator(field);
            if (!success && !orderEditMode.value) {
              anyErrors = true;
            }
          });
        });
      });

      if (!anyErrors) {
        const { success } = await saveUserDetails();
        if (success) {
          await loadCateringServices();
          setAmountServiceView();
        }
      }

      loading.value = false;
    };

    const fieldChange = field => {
      fieldValidator(field);
      setUserDetailValue(field.name, field.value);
    };

    const cancelLoading = ref(false);

    const cancel = async () => {
      cancelLoading.value = true;
      const { success } = await cancelOrder();
      if (success) {
        setRoomView();
      }
      cancelLoading.value = false;
    };

    const askCancelConfirmation = ref(false);
    const startCancel = () => {
      askCancelConfirmation.value = true;
    };

    const stopCancel = () => {
      askCancelConfirmation.value = false;
    };

    const lockTimeLoading = ref(false);

    const lockTime = async () => {
      lockTimeLoading.value = true;

      const { success: successUserDetailSaving } = await saveUserDetails();

      if (successUserDetailSaving) {
        const { success } = await adminBlockTime();
        if (!success) {
          lockTimeLoading.value = false;
        }
      } else {
        lockTimeLoading.value = false;
      }
    };

    return {
      loading,
      fieldBlocks,
      cancel,
      nextStep,
      fieldChange,
      orderEditMode,
      askCancelConfirmation,
      startCancel,
      stopCancel,
      cancelLoading,
      isAdmin,
      lockTime,
      lockTimeLoading
    };
  }
};
</script>
